type Role = 'admin' | 'manager' | 'cms_user';

type NavItem = {
  title: string;
  href?: string;
  icon?: any;
  info?: any;
  adminOnly?: boolean;
  managerOnly?: boolean;
  managerOnlyStrict?: boolean;
  disabled?: boolean;
  items?: NavItem[];
  featureKey?: string;
  // Remove the item even for admins when its feature is off, instead of
  // keeping it as a disabled entry.
  hideWhenOff?: boolean;
};

// A section can carry its own feature flag: off -> hidden, or disabled for admins.
type Section = { subheader: string; items: NavItem[]; featureKey?: string };

type ShowHide = Record<string, boolean | undefined>;

export const normalizeRole = (raw?: string | string[] | null): Role => {
  const asArray = Array.isArray(raw) ? raw : [raw ?? ''];
  const r =
    asArray.map(s => (s ?? '').toLowerCase().trim()).find(Boolean) || '';

  if (r === 'admin' || r === 'super_admin' || r === 'administrator')
    return 'admin';

  if (r === 'manager' || r === 'cms_manager' || r === 'mgr') return 'manager';

  return 'cms_user';
};

const isAdmin = (r: Role) => r === 'admin';
const isManager = (r: Role) => r === 'manager';

const roleAllows = (item: NavItem, role: Role): boolean => {
  if (item.adminOnly) return isAdmin(role);
  if (item.managerOnlyStrict) return isManager(role); // Only managers, not admins
  if (item.managerOnly) return isManager(role) || isAdmin(role);
  return true;
};

const featureEnabled = (flags: ShowHide, item: NavItem): boolean => {
  if (!item.featureKey) return true;
  const v = flags[item.featureKey];
  return v === undefined ? true : !!v;
};

export function applyMenuFilters(
  sections: Section[],
  showHide: ShowHide,
  rawRole: string | string[] | null | undefined
): Section[] {
  const role = normalizeRole(rawRole);

  const prune = (item: NavItem, inheritedDisabled = false): NavItem | null => {
    if (!roleAllows(item, role)) return null;

    const on = featureEnabled(showHide, item);
    if (!on && (!isAdmin(role) || item.hideWhenOff)) return null;

    const disabled = Boolean(
      item.disabled ||
      inheritedDisabled ||
      (!on && isAdmin(role)) || // feature OFF -> disabled for admin
        (isAdmin(role) && item.adminOnly) // NEW: admin-only -> disabled for admin
    );

    if (item.items?.length) {
      const kids = item.items
        .map(child => prune(child, disabled))
        .filter((x): x is NavItem => x !== null);

      if (!kids.length) {
        if (!isAdmin(role)) return null;
        return { ...item, items: [], disabled: true };
      }
      return { ...item, items: kids, disabled };
    }

    return { ...item, disabled };
  };

  return sections
    .map(section => {
      const on = featureEnabled(showHide, { title: section.subheader, featureKey: section.featureKey });
      if (!on && !isAdmin(role)) return null;
      const items = section.items
        .map(it => prune(it, !on))
        .filter((x): x is NavItem => x !== null);

      if (!items.length && !isAdmin(role)) return null;
      return { ...section, items };
    })
    .filter((x): x is Section => x !== null);
}
